import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import AppLogo from "../assets/AppLogo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { isLoggedIn as checkIsLoggedIn, isAdmin, logout } from "../services/authServices";

const Header = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userIsAdmin, setUserIsAdmin] = useState(false);

  // Re-check auth on every route change - the header stays mounted across
  // navigations, so a mount-only check goes stale after login/logout
  useEffect(() => {
    setIsLoggedIn(checkIsLoggedIn());
    setUserIsAdmin(isAdmin());
    setMobileMenuOpen(false);
  }, [location]);

  // Dropdown content data
  const companyLinks = [
    { name: "What we do", path: "/what-we-do" },
    { name: "Workflow", path: "/workflow" },
    { name: "History", path: "/history" },
  ];

  const serviceLinks = [
    { name: "Logistics", path: "/services/logistics" },
    { name: "Airfreight", path: "/services/airfreight" },
    { name: "Ocean Freight", path: "/services/ocean-freight" },
    { name: "Intermodal", path: "/services/intermodal" },
    { name: "Reefer Express", path: "/services/reefer-express" },
    {
      name: "Warehouse & Distribution",
      path: "/services/warehouse-distribution",
    },
    { name: "Customs Clearance", path: "/services/customs-clearance" },
    {
      name: "International Trade Compliance",
      path: "/services/trade-compliance",
    },
    { name: "Household Removals", path: "/services/household-removals" },
    { name: "Project Cargo", path: "/services/project-cargo" },
    { name: "Consolidated Cargo", path: "/services/consolidated-cargo" },
  ];

  const handleDropdownToggle = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setUserIsAdmin(false);
    navigate("/");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when window resizes above mobile breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900 && mobileMenuOpen) {
        setMobileMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuOpen]);

  return (
    <TheHeader ref={dropdownRef}>
      <div className="top-header">
        <div className="c-t-a">
          {isLoggedIn ? (
            <>
              {userIsAdmin && (
                <button
                  className="managers-btn"
                  onClick={() => navigate("/admin/managers")}
                >
                  App Managers
                </button>
              )}
              <button
                className="dashboard-btn"
                onClick={() => navigate("/admin-dashboard")}
              >
                Dashboard
              </button>
              <button className="logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            <button className="track-btn" onClick={() => navigate("/track-shipment")}>
              Track Shipment
            </button>
          )}
        </div>
      </div>
      <hr />
      <div className="bottom-header">
        <div className="logo">
          <a href="/">
            <img src={AppLogo} alt="Trans World Shipping" />
          </a>
        </div>

        {/* Mobile Hamburger Menu */}
        <div
          className={`hamburger ${mobileMenuOpen ? "active" : ""}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* Navigation Links */}
        <div className={`navlinks ${mobileMenuOpen ? "mobile-open" : ""}`}>
          {/* Company Dropdown */}
          <div className="dropdown">
            <button
              className="dropdown-btn"
              onClick={() => handleDropdownToggle("company")}
            >
              Company
              <span className="arrow">
                {openDropdown === "company" ? "▲" : "▼"}
              </span>
            </button>
            <div
              className={`dropdown-content ${openDropdown === "company" ? "show" : ""}`}
            >
              {companyLinks.map((link, index) => (
                <a key={index} href={link.path}>
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          {/* Service Dropdown */}
          <div className="dropdown">
            <button
              className="dropdown-btn"
              onClick={() => handleDropdownToggle("service")}
            >
              Service{" "}
              <span className="arrow">
                {openDropdown === "service" ? "▲" : "▼"}
              </span>
            </button>
            <div
              className={`dropdown-content service-dropdown ${openDropdown === "service" ? "show" : ""}`}
            >
              <div className="service-grid">
                {serviceLinks.map((link, index) => (
                  <a key={index} href={link.path}>
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <a href="/equipment" className="nav-link">
            Equipments
          </a>
          <a href="/contact" className="nav-link">
            Contact
          </a>

          {/* Mobile Dashboard and Auth Links */}
          {isLoggedIn && (
            <div className="mobile-auth-links">
              {userIsAdmin && (
                <button
                  onClick={() => navigate("/admin/managers")}
                  className="mobile-managers"
                >
                  App Managers
                </button>
              )}
              <button
                onClick={() => navigate("/admin-dashboard")}
                className="mobile-dashboard"
              >
                Dashboard
              </button>
              <button onClick={handleLogout} className="mobile-logout">
                Logout
              </button>
            </div>
          )}
          {!isLoggedIn && (
            <div className="mobile-auth-links">
              <button
                onClick={() => navigate("/track-shipment")}
                className="mobile-track"
              >
                Track Shipment
              </button>
            </div>
          )}
        </div>
      </div>
    </TheHeader>
  );
};

const TheHeader = styled.header`
  width: 100%;
  background: white;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  .top-header {
    width: 100%;
    padding: 0.5rem 10rem;
    display: flex;
    justify-content: flex-end;
    background: #0b1e33;

    .c-t-a {
      display: flex;
      gap: 1rem;

      button {
        padding: 0.3rem 1.5rem;
        background: transparent;
        color: white;
        font-size: 0.85rem;
        border: 1px solid #d4af37;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: #d4af37;
          color: #0b1e33;
          border-color: #d4af37;
        }
      }

      .dashboard-btn {
        background: #d4af37;
        color: #0b1e33;

        &:hover {
          background: #c4a12e;
          transform: translateY(-2px);
        }
      }

      .logout-btn {
        &:hover {
          background: #e53e3e;
          border-color: #e53e3e;
          color: white;
        }
      }

      .managers-btn {
        &:hover {
          background: #d4af37;
          color: #0b1e33;
        }
      }
    }
  }

  hr {
    margin: 0;
    border: none;
    height: 1px;
    background: linear-gradient(90deg, #d4af37, transparent);
  }

  .bottom-header {
    width: 100%;
    padding: 0.8rem 10rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    position: relative;

    .logo {
      width: 12rem;
      flex-shrink: 0;

      img {
        width: 100%;
        height: auto;
        object-fit: contain;
      }
    }

    .navlinks {
      display: flex;
      align-items: center;
      gap: 2rem;

      .nav-link,
      .dropdown-btn {
        color: #0b1e33;
        text-decoration: none;
        font-size: 1rem;
        font-weight: 500;
        transition: color 0.3s ease;
        background: none;
        border: none;
        cursor: pointer;
        font-family: inherit;

        &:hover {
          color: #d4af37;
        }
      }

      .dropdown {
        position: relative;

        .dropdown-btn {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.5rem 0;

          .arrow {
            font-size: 0.7rem;
            transition: transform 0.3s ease;
          }
        }

        .dropdown-content {
          position: absolute;
          top: 100%;
          left: 0;

          /* the wide service panel must anchor right so its hidden box
             never extends past the viewport on laptop widths */
          &.service-dropdown {
            left: auto;
            right: 0;
          }
          background: white;
          min-width: 220px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.3s ease;
          z-index: 100;
          padding: 0.5rem 0;
          border-top: 3px solid #d4af37;

          &.show {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
          }

          a {
            display: block;
            padding: 0.6rem 1.2rem;
            color: #0b1e33;
            text-decoration: none;
            font-size: 0.9rem;
            transition: all 0.2s ease;

            &:hover {
              background: #f5f5f5;
              color: #d4af37;
              padding-left: 1.5rem;
            }
          }

          .service-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            min-width: 400px;
            gap: 0;
            padding: 0.5rem 0;
          }
        }
      }

      .mobile-auth-links {
        display: none;
      }
    }

    .hamburger {
      display: none;
      flex-direction: column;
      cursor: pointer;
      z-index: 101;

      span {
        width: 25px;
        height: 3px;
        background: #0b1e33;
        margin: 2px 0;
        transition: all 0.3s ease;
        border-radius: 2px;
      }

      &.active {
        span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }
        span:nth-child(2) {
          opacity: 0;
        }
        span:nth-child(3) {
          transform: rotate(-45deg) translate(7px, -7px);
        }
      }
    }
  }

  /* Responsive Styles */
  @media screen and (max-width: 1366px) {
    .top-header {
      padding: 0.5rem 5rem !important;
    }
    .bottom-header {
      padding: 0.8rem 5rem !important;
    }
  }

  @media screen and (max-width: 1200px) {
    .top-header {
      padding: 0.5rem 3rem !important;
    }
    .bottom-header {
      padding: 0.8rem 3rem !important;
    }
    .navlinks {
      gap: 1.2rem !important;
    }
    .dropdown-content .service-grid {
      min-width: 350px !important;
    }
  }

  @media screen and (max-width: 900px) {
    .hamburger {
      display: flex !important;
    }

    .navlinks {
      position: fixed;
      top: 0;
      right: -100%;
      width: 80%;
      max-width: 400px;
      height: 100vh;
      background: white;
      flex-direction: column;
      align-items: flex-start;
      padding: 5rem 2rem 2rem;
      gap: 1rem;
      transition: right 0.3s ease;
      box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
      z-index: 100;
      overflow-y: auto;

      &.mobile-open {
        right: 0;
      }

      .nav-link,
      .dropdown {
        width: 100%;
      }

      .dropdown-btn {
        width: 100%;
        justify-content: space-between;
        padding: 0.8rem 0 !important;
        font-size: 1.1rem !important;
      }

      .dropdown-content {
        position: static;
        box-shadow: none;
        opacity: 1;
        visibility: visible;
        transform: none;
        display: none;
        padding-left: 1rem;
        border-left: 2px solid #d4af37;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;

        &.show {
          display: block;
        }

        .service-grid {
          grid-template-columns: 1fr;
          min-width: auto;
        }
      }

      .mobile-auth-links {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #e2e8f0;

        button {
          padding: 0.75rem;
          width: 100%;
          text-align: center;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .mobile-dashboard {
          background: #d4af37;
          color: #0b1e33;
          border: none;

          &:hover {
            background: #c4a12e;
          }
        }

        .mobile-logout {
          background: transparent;
          color: #e53e3e;
          border: 1px solid #e53e3e;

          &:hover {
            background: #e53e3e;
            color: white;
          }
        }

        .mobile-managers {
          background: transparent;
          color: #0b1e33;
          border: 1px solid #d4af37;

          &:hover {
            background: #d4af37;
            color: #0b1e33;
          }
        }

        .mobile-track {
          background: transparent;
          color: #0b1e33;
          border: 1px solid #d4af37;

          &:hover {
            background: #d4af37;
            color: #0b1e33;
          }
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .top-header {
      padding: 0.4rem 1rem !important;
      .c-t-a {
        gap: 0.5rem;
        button {
          padding: 0.2rem 0.8rem;
          font-size: 0.7rem;
        }
      }
    }
    .bottom-header {
      padding: 0.6rem 1rem !important;
      .logo {
        width: 9rem;
      }
    }
  }

  @media screen and (max-width: 420px) {
    .top-header {
      padding: 0.3rem 0.8rem !important;
    }
    .bottom-header {
      padding: 0.5rem 0.8rem !important;
      .logo {
        width: 7.5rem;
      }
    }
    .navlinks {
      width: 85%;
    }
  }

  @media screen and (max-width: 350px) {
    .top-header {
      padding: 0.3rem 0.5rem !important;
      .c-t-a {
        gap: 0.3rem;
        button {
          padding: 0.2rem 0.5rem;
          font-size: 0.65rem;
        }
      }
    }
    .bottom-header {
      padding: 0.5rem 0.5rem !important;
      .logo {
        width: 6.5rem;
      }
    }
  }
`;

export default Header;
